import { mediaQueryLarge, prefersReducedMotion, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer, buildSectionSelector } from '@theme/section-renderer';
import { expandGrapeColourInSearchParams, sortProductGridByDisplayPrice } from '@theme/facets';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {AbortController | null} */
  #paginationClickHandler = null;

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    this.#setupPaginationFilterPreservation();
    this.#runInitialPriceSortIfNeeded();
  }

  /**
   * Facets form runs sort in rAF on connect; this runs from results-list after the grid subtree exists
   * so price-descending / price-ascending order matches data-sort-price on the cards.
   */
  #runInitialPriceSortIfNeeded() {
    const sortBy = new URL(window.location.href).searchParams.get('sort_by') || '';
    const isPrice =
      sortBy === 'price-descending' ||
      sortBy === 'price-ascending' ||
      sortBy === 'price-desc' ||
      sortBy === 'price-asc';
    if (!isPrice) return;

    const sectionId = this.sectionId;
    const runSort = () => {
      const section = document.getElementById(buildSectionSelector(sectionId));
      if (section) sortProductGridByDisplayPrice(section, sortBy);
    };
    queueMicrotask(runSort);
    requestAnimationFrame(() => {
      runSort();
      requestAnimationFrame(runSort);
    });
    setTimeout(runSort, 0);
    setTimeout(runSort, 50);
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.#paginationClickHandler?.abort();
    super.disconnectedCallback();
  }

  /**
   * Offset for sticky header (announcement bar + nav) so results are not hidden underneath.
   * @returns {number}
   */
  #getHeaderScrollOffset() {
    const bodyStyles = getComputedStyle(document.body);
    const headerGroup = Number.parseFloat(bodyStyles.getPropertyValue('--header-group-height')) || 0;
    const headerOnly = Number.parseFloat(bodyStyles.getPropertyValue('--header-height')) || 0;
    const base = headerGroup > 0 ? headerGroup : headerOnly;
    return base + 32;
  }

  /**
   * Scroll to the top of the product results (filters stay above on collection pages).
   */
  #scrollToResultsTop() {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    const offset = this.#getHeaderScrollOffset();
    const target =
      this.querySelector('#ResultsList') ??
      document.getElementById('ResultsList') ??
      document.getElementById('MainContent');
    if (!target) {
      window.scrollTo({ top: 0, behavior });
      return;
    }
    const top = target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, top), behavior });
  }

  /**
   * Intercepts pagination link clicks to preserve filters when navigating.
   * Uses Section Rendering API so filters/sort stay applied without full page reload.
   */
  #setupPaginationFilterPreservation() {
    this.#paginationClickHandler?.abort();
    this.#paginationClickHandler = new AbortController();

    this.addEventListener(
      'click',
      (e) => {
        const link = e.target.closest?.('a.collection-pagination__link, a.collection-pagination__number');
        if (!link?.href) return;

        const linkUrl = new URL(link.href, window.location.origin);
        const currentUrl = new URL(window.location.href);

        if (linkUrl.pathname !== currentUrl.pathname) return;

        const linkPage = linkUrl.searchParams.get('page') || '1';
        const currentPage = currentUrl.searchParams.get('page') || '1';
        if (linkPage === currentPage) return;

        e.preventDefault();

        // Build target URL: current path + all current query params (sort_by, filters, etc.), only change page.
        // Use append (not set) so repeated filter params (e.g. grape_colour Blanc + White) are preserved.
        const targetUrl = new URL(currentUrl.pathname, currentUrl.origin);
        for (const [key, value] of currentUrl.searchParams) {
          if (key === 'page') continue;
          targetUrl.searchParams.append(key, value);
        }
        expandGrapeColourInSearchParams(targetUrl.searchParams);
        targetUrl.searchParams.set('page', linkPage);
        targetUrl.hash = '';

        history.pushState({}, '', targetUrl.toString());
        const requestedSortBy = targetUrl.searchParams.get('sort_by') || '';
        sectionRenderer.renderSection(this.sectionId, { url: targetUrl, cache: false }).then(() => {
          window.dispatchEvent(new CustomEvent('section-rendered', { detail: { sectionId: this.sectionId } }));
          this.#scrollToResultsTop();
          // Re-sort by display price after pagination; use requested URL's sort_by and run next tick so morphed DOM is ready
          const sectionId = this.sectionId;
          const runSort = () => {
            const section = document.getElementById(buildSectionSelector(sectionId));
            if (section) sortProductGridByDisplayPrice(section, requestedSortBy);
          };
          requestAnimationFrame(() => {
            runSort();
            requestAnimationFrame(runSort); // second rAF in case grid refs/children settle after first paint
          });
        });
      },
      { capture: true, signal: this.#paginationClickHandler.signal }
    );
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
